import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { DEVTOOLS_OPTIONS } from '@/modules/debug/constants/zustand';
import { Mode, ScaleDraft, UploadProcess } from '../types';

export type State = {
  referenceImage: {
    /**
     * @description
     * controls whether the required reference scaling dialog is open or not
     */
    requiredScalingDialogOpenState: boolean;
    mode: Mode;
    uploadProcess: UploadProcess;
    /**
     * @description
     * original dimensions of the reference image in millimeter
     */
    originalDimensions: {
      width: number;
      height: number;
    } | null;
    fileDisplayName: string;
    /**
     * @description temp data needed to scale the reference image. Discarded after scaling flow
     */
    scaleDraft: ScaleDraft | null;
  };
};

type Actions = {
  referenceImageActions: {
    setRequiredScalingDialogOpenState(value: boolean): void;
    setMode(value: Mode): void;
    setUploadProcess(value: UploadProcess): void;
    setOriginalDimensions(value: { width: number; height: number }): void;
    setFileDisplayName(value: string): void;
    setScaleDraft(
      valueOrUpdater:
        | State['referenceImage']['scaleDraft']
        | ((
            current: State['referenceImage']['scaleDraft'],
          ) => State['referenceImage']['scaleDraft']),
    ): void;
  };
  reset(): void;
};

const INITIAL_STATE: State = {
  referenceImage: {
    requiredScalingDialogOpenState: false,
    mode: 'upload',
    originalDimensions: null,
    fileDisplayName: '',
    uploadProcess: {
      status: 'idle',
    },
    scaleDraft: null,
  },
};

export const useStore = create<State & Actions>()(
  devtools(
    (set, get) => ({
      ...INITIAL_STATE,

      referenceImageActions: {
        setRequiredScalingDialogOpenState(value) {
          set(
            (state): State => ({
              referenceImage: {
                ...state.referenceImage,
                requiredScalingDialogOpenState: value,
              },
            }),
            undefined,
            {
              type: 'referenceImage/requiredScalingDialogOpenState/set',
            },
          );
        },

        setMode(value) {
          set(
            (state): State => ({
              referenceImage: {
                ...state.referenceImage,
                mode: value,
              },
            }),
            undefined,
            {
              type: 'referenceImage/mode/set',
            },
          );
        },

        setUploadProcess(value) {
          set(
            (state): State => ({
              referenceImage: {
                ...state.referenceImage,
                uploadProcess: value,
              },
            }),
            undefined,
            {
              type: 'referenceImage/uploadProcess/set',
            },
          );
        },

        setOriginalDimensions(value) {
          set(
            (state): State => ({
              referenceImage: {
                ...state.referenceImage,
                originalDimensions: value,
              },
            }),
            undefined,
            {
              type: 'referenceImage/dimensions/original/set',
            },
          );
        },

        setFileDisplayName(value) {
          set(
            (state): State => ({
              referenceImage: {
                ...state.referenceImage,
                fileDisplayName: value,
              },
            }),
            undefined,
            {
              type: 'referenceImage/fileDisplayName/set',
            },
          );
        },

        setScaleDraft(valueOrUpdater) {
          set(
            (state): State => ({
              referenceImage: {
                ...state.referenceImage,
                scaleDraft:
                  typeof valueOrUpdater === 'function'
                    ? valueOrUpdater(get().referenceImage.scaleDraft)
                    : valueOrUpdater,
              },
            }),
            undefined,
            {
              type: 'referenceImage/scaleDraft/set',
            },
          );
        },
      },

      reset() {
        set(INITIAL_STATE, undefined, {
          type: 'reset',
        });
      },
    }),
    { store: 'referenceImageUIStore', ...DEVTOOLS_OPTIONS },
  ),
);
